import { z } from "zod";
import { DEFAULT_PRODUCT, PRODUCTS, type Product } from "./product.js";

// ---------------------------------------------------------------------------
// 分割請求（#44・#76 の2回目、042、docs/30）。
//   案件の「請求タイミング」で 一括／分割 を選び、分割のときは 受注した見積ごとに「分割予定」を作る。
//   回ごとに 割合（%）か 税込の金額。最後の回は「残り」。金額は DB（fn_installment_calc）だけが計算する。
//   請求書は予定の回の順に1枚ずつ（それぞれ、ふつうの適格請求書）。
// ---------------------------------------------------------------------------

/** 画面に出す文はここに1か所（docs/30 付録C） */
export const INSTALLMENT_MSG = {
  notFound: "その分割の予定は見つかりません",
  planNotFound: "この見積には分割の予定がありません",
  notOrdered: "受注にした見積だけ、分割の予定を作れます",
  tooFew: "分割は2回以上です",
  tooMany: "分割は12回までです",
  lastMustBeRest: "最後の回は「残り」です",
  restOnlyLast: "「残り」にできるのは最後の回だけです",
  labelTooLong: "回の名前は20文字までです",
  labelRequired: "回の名前を入れてください",
  defaultsSum: "割合の合計は 100% より小さくしてください（最後の回が残りです）",
  percentRange: "0 より大きく 100 より小さい割合を入れてください",
  amountRange: "税込の金額を入れてください",
  amountMixedRate: "税率が2つある見積は、割合でだけ分けられます",
  percentTotal: "割合の合計が 100% 以上です。最後の「残り」が無くなります",
  restEmpty: "ほかの回で見積の金額を使い切っています。最後の「残り」が 0円以下です",
  locked: "請求書を起こした回は、ここでは修正できません。金額はその請求書で修正します",
  hasInvoice: "請求書を起こした回があるので、分割の予定は消せません",
  /** T1：立替金の行がある見積には分割の予定を作れない（第1版の制限。DB の INSTALLMENT_HAS_REIMBURSEMENT） */
  hasReimbursement: "立替金の行がある見積は、分割の予定を作れません（一括で請求します）",
  order: "前の回の請求書をまだ起こしていません",
  draftExists: "この見積には書きかけの請求書があります。そちらをひらきます",
  timingLocked: "請求書を起こした回がある見積があるので、一括に戻せません",
  /** 税込の金額で打った回（1円直したとき） */
  amountAdjusted: (want: number, got: number): string => `${want.toLocaleString()} 円ちょうどにはできないため、${got.toLocaleString()} 円にします`,
  /** 合計が見積と合っているか（#171 から合計の横のチップ。下の2つはチップの title） */
  matchChip: "✓ 合計と一致",
  diffChip: (yen: number): string => `差額 ${Math.abs(yen).toLocaleString()}円`,
  exact: "✓ 税抜・消費税・税込とも、見積とぴったり同じです",
  diff: (yen: number): string =>
    `消費税と税込の合計が、見積より ${Math.abs(yen).toLocaleString()} 円${yen < 0 ? "少なく" : "多く"}なります（税抜の合計は同じ）`,
} as const;

/** 請求タイミング（案件）：一括／分割（042）／定期（#268、契約の雛形 recurring_plans から毎月の請求書。sales の会社だけ選べる） */
export const BillingTiming = z.enum(["lump", "split", "recurring"]);
export type BillingTiming = z.infer<typeof BillingTiming>;
export const BILLING_TIMING_LABEL: Record<BillingTiming, string> = { lump: "一括", split: "分割", recurring: "定期" };

/** 回の分け方。rest＝見積の残り（最後の回） */
export const InstallmentKind = z.enum(["percent", "amount", "rest"]);
export type InstallmentKind = z.infer<typeof InstallmentKind>;

/** 予定の1回（送るとき） */
export const InstallmentInput = z.object({
  label: z.string().trim().max(20, INSTALLMENT_MSG.labelTooLong).default(""),
  kind: InstallmentKind,
  /** 割合（%）。kind=percent のとき */
  percent: z.number().nullable().default(null),
  /** 税込の金額。kind=amount のとき */
  amountIncl: z.number().int().nullable().default(null),
  /** 請求予定日 */
  plannedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
});
export type InstallmentInput = z.infer<typeof InstallmentInput>;

export const InstallmentSave = z.object({
  items: z.array(InstallmentInput).min(2, INSTALLMENT_MSG.tooFew).max(12, INSTALLMENT_MSG.tooMany),
});
export type InstallmentSave = z.infer<typeof InstallmentSave>;

/** 予定の1回（返すとき）。金額は DB が計算したもの */
export const InstallmentRow = InstallmentInput.extend({
  seq: z.number().int(),
  subtotal: z.number().int(),
  tax: z.number().int(),
  total: z.number().int(),
  /** 税込の金額をちょうどにできず1円直したときの文（無ければ null） */
  note: z.string().nullable(),
  /** その回の間違い（割合が範囲外・税率が2つあるのに金額指定・残りが0円以下） */
  error: z.string().nullable(),
  /** この回の請求書（起こしていれば） */
  invoice: z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), status: z.string(), total: z.number().int() }).nullable(),
  /** 請求書を起こした回は、予定の上で直せない */
  locked: z.boolean(),
});
export type InstallmentRow = z.infer<typeof InstallmentRow>;

/** 分割の予定（GET /estimates/:id/billing-plan、POST …/preview） */
export const BillingPlan = z.object({
  quoteId: z.string().uuid(),
  quoteNo: z.string().nullable(),
  projectId: z.string().uuid(),
  subject: z.string(),
  items: z.array(InstallmentRow),
  /** ご契約金額（受注した見積の出ている回） */
  contract: z.object({ subtotal: z.number().int(), tax: z.number().int(), total: z.number().int() }),
  /** 各回の合計 */
  sum: z.object({ subtotal: z.number().int(), tax: z.number().int(), total: z.number().int() }),
  /** 見積との差（0 なら ぴったり） */
  diff: z.object({ subtotal: z.number().int(), tax: z.number().int(), total: z.number().int() }),
  /** 出した回の数 */
  issuedCount: z.number().int(),
  /** 次に起こす回（無ければ null）。下書きがあるときは、その下書き */
  next: z.object({ id: z.string().uuid(), seq: z.number().int(), label: z.string(), total: z.number().int(), draftInvoiceId: z.string().uuid().nullable() }).nullable(),
  /** 予定ぜんたいの間違い（保存できない） */
  error: z.string().nullable(),
  /** 予定を消して一括に戻せるか（どの回も起こしていないとき） */
  canDelete: z.boolean(),
});
export type BillingPlan = z.infer<typeof BillingPlan>;

/** 予定のあらまし（見積の一覧・見積の画面・請求書の画面の帯で使う）。next＝次に起こす回（無ければ null＝すべて起こした） */
export const PlanBrief = z.object({
  count: z.number().int(),
  issuedCount: z.number().int(),
  next: z.object({ id: z.string().uuid(), seq: z.number().int(), label: z.string(), total: z.number().int(), draftInvoiceId: z.string().uuid().nullable() }).nullable(),
});
export type PlanBrief = z.infer<typeof PlanBrief>;
export const planBriefOf = (p: BillingPlan): PlanBrief => ({ count: p.items.length, issuedCount: p.issuedCount, next: p.next });

/** 保存しないで計算だけ（画面の右の列） */
export const BillingPlanPreview = InstallmentSave;
export type BillingPlanPreview = z.infer<typeof BillingPlanPreview>;

/** 請求書の紙の上の欄（ご契約金額／これまでのご請求／今回のご請求／残り）。出した時点で固まる */
export const InstallmentFigures = z.object({
  seq: z.number().int(),
  count: z.number().int(),
  label: z.string(),
  contract: z.number().int(),
  before: z.number().int(),
  now: z.number().int(),
  rest: z.number().int(),
  /** 出したあとに固めた数字か */
  frozen: z.boolean(),
});
export type InstallmentFigures = z.infer<typeof InstallmentFigures>;

/**
 * 会社の「分割の既定」の1回（#142 段3、docs/52 §6。tenants.installment_defaults）。percent null＝残り（最後の回だけ）。
 * 形の決まりは DB の fn_installment_defaults_ok と同じ（2〜12回・名前は 1〜20 文字・最後だけ残り・割合は 0〜100・合計 100 未満）
 */
export const InstallmentDefault = z.object({
  label: z.string().trim().min(1, INSTALLMENT_MSG.labelRequired).max(20, INSTALLMENT_MSG.labelTooLong),
  percent: z.number().gt(0, INSTALLMENT_MSG.percentRange).lt(100, INSTALLMENT_MSG.percentRange).nullable(),
});
export type InstallmentDefault = z.infer<typeof InstallmentDefault>;
export const InstallmentDefaults = z
  .array(InstallmentDefault)
  .min(2, INSTALLMENT_MSG.tooFew)
  .max(12, INSTALLMENT_MSG.tooMany)
  .superRefine((xs, ctx) => {
    xs.forEach((x, i) => {
      const last = i === xs.length - 1;
      if (last && x.percent !== null) ctx.addIssue({ code: "custom", path: [i, "percent"], message: INSTALLMENT_MSG.lastMustBeRest });
      if (!last && x.percent === null) ctx.addIssue({ code: "custom", path: [i, "percent"], message: INSTALLMENT_MSG.restOnlyLast });
    });
    const sum = xs.slice(0, -1).reduce((a, x) => a + (x.percent ?? 0), 0);
    if (sum >= 100) ctx.addIssue({ code: "custom", path: [], message: INSTALLMENT_MSG.defaultsSum });
  });
export type InstallmentDefaults = z.infer<typeof InstallmentDefaults>;

/** 製品の既定の回（名前・割合）。現場力＝着手金・中間金・完成時、営業力＝前金・中間金・残金（PRODUCTS[*].installments） */
export const productInstallmentDefaults = (product: Product = DEFAULT_PRODUCT): InstallmentDefault[] =>
  PRODUCTS[product].installments.map(({ label, percent }) => ({ label, percent }));

/** 会社の既定（設定 → 見積・請求）が無ければ製品の既定 */
export const installmentDefaultsOf = (product: Product = DEFAULT_PRODUCT, company?: InstallmentDefault[] | null): InstallmentDefault[] =>
  company && company.length >= 2 ? company : productInstallmentDefaults(product);

/**
 * 既定の予定（docs/30 受け入れ条件2）。製品ごと（#142 段1、docs/52 §6）：
 *   現場力＝着手金 30%・中間金 30%・完成時 残り、営業力＝前金 30%・中間金 30%・残金 残り（PRODUCTS[*].installments）
 *   会社の既定（#142 段3）があればそちら。請求予定日は 1回目＝今日、2回目から1か月ずつ、最後は前の回の2か月後（3回なら 0・1・3か月後）
 */
export function defaultInstallments(today: string, product: Product = DEFAULT_PRODUCT, company?: InstallmentDefault[] | null): InstallmentInput[] {
  const d = (months: number): string => {
    const t = new Date(`${today}T00:00:00`);
    t.setMonth(t.getMonth() + months);
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  };
  const xs = installmentDefaultsOf(product, company);
  const monthsOf = (i: number) => (i < xs.length - 1 ? i : Math.max(0, xs.length - 2) + 2);
  return xs.map((x, i) =>
    x.percent === null
      ? { label: x.label, kind: "rest", percent: null, amountIncl: null, plannedOn: d(monthsOf(i)) }
      : { label: x.label, kind: "percent", percent: x.percent, amountIncl: null, plannedOn: d(monthsOf(i)) },
  );
}

/** 既定の回の名前を「・」でつないだもの（説明の文に。「着手金・中間金・完成時」） */
export const installmentDefaultNames = (product: Product = DEFAULT_PRODUCT, company?: InstallmentDefault[] | null): string =>
  installmentDefaultsOf(product, company)
    .map((x) => x.label)
    .join("・");

/** 「1／3回 請求済」（#187 で「請求済み」から） */
export const installmentProgressLabel = (issued: number, count: number): string => `${issued}／${count}回 請求済`;
